import { Children, isValidElement, forwardRef, useEffect, useImperativeHandle } from "react";
import { margin, layout } from "styled-system";
import styled from "styled-components";
import { useControllableState, usePrevious } from "../../hooks";
import { isDefined, isString } from "../../utils/type-util";
import { FilterOperator } from "../../enums/filter-operator";
import ClickAwayListener from "../click-away-listener";
import FilterOption from "./filter-option";
import FilterBar from "./filter-bar";
import FilterPanel from "./filter-panel";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Container = styled.div.withConfig({
  displayName: "filter__Container",
  componentId: "sc-znxhvl-0"
})(["", " ", ""], margin, layout);
export const Filter = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    value,
    defaultValue,
    onChange,
    selectedKey,
    defaultSelectedKey,
    onSelect,
    label = "Filter by",
    emptyText = "All",
    allowAdd = true,
    showSearch = false,
    autoClose = true,
    extra,
    children,
    ...rest
  } = _ref;
  const [selectedIndex, setSelectedIndex] = useControllableState(selectedKey, onSelect, defaultSelectedKey ?? null);
  const [descriptors, setDescriptors] = useControllableState(value, onChange, defaultValue ?? []);
  const prevSelectedIndex = usePrevious(selectedIndex);
  const options = Children.toArray(children).filter(el => /*#__PURE__*/isValidElement(el)).map(el => {
    // @ts-ignore
    return {
      key: el.key,
      ...el.props
    };
  });
  useEffect(() => {
    if (prevSelectedIndex && prevSelectedIndex !== selectedIndex) {
      const opt = options.find(opt => opt.dataIndex === prevSelectedIndex);
      const desc = descriptors.find(desc => desc.dataIndex === prevSelectedIndex);
      if (opt?.removable && desc && (Array.isArray(desc.value) || isString(desc.value)) && desc.value.length === 0) {
        setDescriptors(descriptors.filter(desc => desc.dataIndex !== prevSelectedIndex));
      }
    }
  }, [prevSelectedIndex, selectedIndex]);
  const handleClickAway = event => {
    if (autoClose && !event.defaultPrevented) {
      setSelectedIndex(null);
    }
  };
  const addFilter = key => {
    const opt = options.find(opt => opt.dataIndex === key);
    const desc = descriptors.find(desc => desc.dataIndex === key);
    if (opt) {
      if (!desc) {
        const nextState = descriptors.concat({
          dataIndex: opt.dataIndex,
          operator: isDefined(opt.defaultOperator) ? opt.defaultOperator : "",
          value: isDefined(opt.defaultValue) ? opt.defaultValue : ""
        });
        setDescriptors(nextState);
      }
      setSelectedIndex(key);
    }
  };
  const removeFilter = key => {
    setDescriptors(descriptors.filter(desc => desc.dataIndex !== key));
    if (key === selectedIndex) {
      setSelectedIndex(null);
    }
  };
  const updateFilter = (key, props) => {
    const desc = descriptors.find(desc => desc.dataIndex === key);
    if (desc) {
      const nextState = descriptors.slice();
      nextState.splice(nextState.indexOf(desc), 1, {
        ...desc,
        ...props
      });
      setDescriptors(nextState);
    }
  };
  useImperativeHandle(ref, () => ({
    addFilter
  }));
  return /*#__PURE__*/_jsx(ClickAwayListener, {
    onClickAway: handleClickAway,
    children: /*#__PURE__*/_jsxs(Container, {
      ...rest,
      children: [/*#__PURE__*/_jsx(FilterBar, {
        options: options,
        descriptors: descriptors,
        selectedIndex: selectedIndex,
        label: label,
        emptyText: emptyText,
        allowAdd: allowAdd,
        showSearch: showSearch,
        onSelect: setSelectedIndex,
        onAdd: addFilter,
        onRemove: removeFilter,
        extra: extra
      }), /*#__PURE__*/_jsx(FilterPanel, {
        options: options,
        descriptors: descriptors,
        selectedIndex: selectedIndex,
        onSelect: setSelectedIndex,
        updateFilter: updateFilter
      })]
    })
  });
});
export default Object.assign(Filter, {
  Operator: FilterOperator,
  Option: FilterOption
});